import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, Fish, MapPin } from "lucide-react";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import { toast } from "sonner";
import { api, errMsg, photoUrl, fmtDate } from "../lib/api";
import { KeeperBadge, EdibleBadge, KEEPER_DISCLAIMER } from "../components/Badges";
import { CatchForm } from "../components/CatchForm";
import { WeatherGrid } from "../components/WeatherGrid";
import { TILE_URL, TILE_ATTR } from "../components/MapPicker";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-[#27272A] text-sm" data-testid={testId}>
      <span className="text-zinc-400 uppercase text-xs font-bold tracking-widest">{label}</span>
      <span className="text-right">{value || "—"}</span>
    </div>
  );
}

export default function CatchDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [spots, setSpots] = useState([]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/catches/${id}`).then((r) => setC(r.data)).catch(() => { toast.error("Catch not found"); nav("/log"); });
    api.get("/spots").then((r) => setSpots(r.data)).catch(() => {});
  }, [id, nav]);

  const save = async (form) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/catches/${id}`, form);
      setC(data);
      setEditing(false);
      toast.success("Catch updated");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/catches/${id}`);
      toast.success("Catch deleted");
      nav("/log");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!c) return <p className="text-zinc-500" data-testid="catch-detail-loading">Loading…</p>;

  return (
    <div className="space-y-6">
      <Link to="/log" className="inline-flex items-center gap-2 text-sm font-bold uppercase text-zinc-400 hover:text-white" data-testid="catch-detail-back-link">
        <ArrowLeft size={16} /> Log
      </Link>
      <div className="brut-card-hot overflow-hidden">
        <div className="aspect-[4/3] bg-[#1E1E24]">
          {c.photo_path ? <img src={photoUrl(c.photo_path)} alt={c.species} className="w-full h-full object-cover" data-testid="catch-detail-photo" />
            : <div className="w-full h-full flex items-center justify-center text-zinc-600"><Fish size={56} /></div>}
        </div>
        <div className="p-5 space-y-3">
          <h1 className="font-display text-3xl uppercase leading-none" data-testid="catch-detail-species">{c.species}</h1>
          {c.scientific_name && <p className="text-sm italic text-zinc-400">{c.scientific_name}</p>}
          <div className="flex flex-wrap gap-2">
            <EdibleBadge edible={c.edible} testId="catch-detail-edible" />
            {c.keeper_status && <KeeperBadge status={c.keeper_status} testId="catch-detail-keeper" />}
          </div>
        </div>
      </div>

      {editing ? (
        <div className="brut-card p-5">
          <h2 className="brut-h2 mb-6">Edit catch</h2>
          <CatchForm initial={c} spots={spots} onSubmit={save} busy={busy} submitLabel="Save changes" />
          <button className="brut-btn-ghost w-full mt-4" onClick={() => setEditing(false)} data-testid="catch-edit-cancel-btn">Cancel</button>
        </div>
      ) : (
        <>
          <div className="brut-card p-5">
            <Row label="Length" value={c.length_in != null ? `${c.length_in} in` : null} testId="catch-detail-length" />
            <Row label="Bait" value={c.bait} testId="catch-detail-bait" />
            <Row label="Spot" value={c.spot_name} testId="catch-detail-spot" />
            <Row label="Caught" value={fmtDate(c.caught_at)} testId="catch-detail-date" />
            <Row label="Region" value={c.region} testId="catch-detail-region" />
            {c.keeper_note && <p className="text-sm text-zinc-300 pt-3">{c.keeper_note}</p>}
            {c.notes && <p className="text-sm text-zinc-300 pt-3 whitespace-pre-wrap" data-testid="catch-detail-notes">{c.notes}</p>}
            {c.keeper_status && <p className="text-xs text-[#EAB308] pt-3">{KEEPER_DISCLAIMER}</p>}
          </div>

          <div className="space-y-3">
            <h2 className="brut-h2">Conditions at catch</h2>
            <WeatherGrid w={c.weather} tide={c.tide} prefix="catch-weather" />
          </div>

          {c.lat != null && (
            <div className="border-2 border-[#27272A] h-56" data-testid="catch-detail-map">
              <MapContainer center={[c.lat, c.lng]} zoom={12} className="h-full w-full" scrollWheelZoom={false}>
                <TileLayer url={TILE_URL} attribution={TILE_ATTR} />
                <CircleMarker center={[c.lat, c.lng]} radius={10} pathOptions={{ color: "#000", fillColor: "#FF4D00", fillOpacity: 1, weight: 3 }} />
              </MapContainer>
            </div>
          )}
          {c.lat == null && <p className="text-xs text-zinc-500 flex items-center gap-2"><MapPin size={14} /> No GPS pin — edit to add one.</p>}

          <div className="grid grid-cols-2 gap-4">
            <button className="brut-btn" onClick={() => setEditing(true)} data-testid="catch-edit-btn"><Pencil size={16} /> Edit</button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="brut-btn-ghost !border-[#EF4444] !text-[#EF4444] hover:!bg-[#EF4444] hover:!text-white" data-testid="catch-delete-btn">
                  <Trash2 size={16} /> Delete
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none border-2 border-[#FF4D00]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display uppercase">Delete this catch?</AlertDialogTitle>
                  <AlertDialogDescription>This removes it from your log and the leaderboard.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none" data-testid="catch-delete-cancel-btn">Keep it</AlertDialogCancel>
                  <AlertDialogAction className="rounded-none bg-[#EF4444] text-white hover:bg-[#dc2626]" onClick={remove} data-testid="catch-delete-confirm-btn">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </>
      )}
    </div>
  );
}
